import {
  clinicalTasksActiveSheetTypeAtom,
  clinicalTasksCurrentClinicalTaskAtom,
  clinicalTasksTaskSheetOpenAtom,
} from '@/atoms/clinicalTasksAtoms';
import { formFillingUploadModalOpenAtom } from '@/atoms/formFillingAtom';
import { createTemplateModalAtom } from '@/atoms/templateBuilderAtoms';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { MenuCoachmarkCreateStep } from '@/components/layout/Navigation/coachmark';
import { useChatSidebarHandlers } from '@/components/layout/Navigation/hooks/useChatSidebarHandlers';
import { useOpenRoutineCreateChat } from '@/composition/hosts/agents/routines/useOpenRoutineCreateChat';
import { sessionListHost } from '@/composition/hosts/session/session-list/session-list-host';
import {
  IS_CLINICAL_TASKS_ENABLED,
  IS_TEMPLATE_CREATION_RECORD_ENABLED,
  useIsPdfFormDisabled,
  useIsRibbonSidebarEnabled,
  useIsWorkflowsEnabled,
} from '@/features/flags';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useNewSessionAction } from '@/hooks/useNewSessionAction';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { cn } from '@/lib/cn';
import { SessionCreatedEntryPoint } from '@/models/analytics/features/sessions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem } from '@heidi/design-system/page-shell';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { ClinicianSelectorPopover } from '@heidi/session';
import { useSetAtom } from 'jotai';
import { Loader2, Plus } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { CreateMenuItems } from './create-menu-items';

type SidenavCreateOption =
  | 'open'
  | 'session'
  | 'chat'
  | 'task'
  | 'routine'
  | 'template'
  | 'pdf_form';
interface CreateButtonProps {
  className?: string;
}

const createMenuMessages = defineMessages({
  xmlForm: {
    defaultMessage: 'Form',
    id: 'h5xknHN4ek',
    description:
      'Universal create-menu item that starts form-template upload on a build where only XML forms are supported',
  },
});

export function CreateButton({ className }: CreateButtonProps) {
  const { t } = useTranslations();
  const intl = useIntl();
  const router = useRouter();
  const isMobile = useIsMobile();

  const trackCreateMenu = useCallback((label: SidenavCreateOption) => {
    trackButtonClicked({
      screen_name: ScreenName.Sidenav,
      action: ButtonAction.SidenavCreateMenuClicked,
      label,
    });
  }, []);
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
  const setCreateTemplateModal = useSetAtom(createTemplateModalAtom);
  const setTaskSheetOpen = useSetAtom(clinicalTasksTaskSheetOpenAtom);
  const setActiveSheetType = useSetAtom(clinicalTasksActiveSheetTypeAtom);
  const setCurrentTask = useSetAtom(clinicalTasksCurrentClinicalTaskAtom);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [clinicianOpen, setClinicianOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusOnCloseRef = useRef(false);
  // Set when the Session item is picked in clinician-picker mode. The popover is
  // opened from the menu's onCloseAutoFocus (after the menu fully tears down),
  // not from the item click — see handleNewSession.
  const openClinicianOnCloseRef = useRef(false);

  const action = useNewSessionAction(SessionCreatedEntryPoint.CreateButton);

  const setFormFillingUploadModalOpen = useSetAtom(formFillingUploadModalOpenAtom);

  const { isSessionlessEvidenceShown: isEvidenceEnabled } = useEvidenceAccess();
  const isTasksEnabled = IS_CLINICAL_TASKS_ENABLED();
  const isWorkflowsEnabled = useIsWorkflowsEnabled();
  const isTemplateCreationRecordEnabled = IS_TEMPLATE_CREATION_RECORD_ENABLED();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const isPdfFormDisabled = useIsPdfFormDisabled();
  const dropdownSideOffset = isRibbonSidebarEnabled && !isMobile ? 12 : 4;
  const { handleNewChat } = useChatSidebarHandlers();

  const openRoutineCreateChat = useOpenRoutineCreateChat();

  const { shouldGateControls: isOfflineGated } = useOfflineGating();

  const isSessionHidden = action.mode === 'hidden';
  const isSessionPending = action.mode === 'hidden' ? false : action.isPending;
  const isSessionDisabled = action.mode !== 'hidden' && action.isDisabled;
  const sessionAriaLabel =
    action.mode === 'directCreate' && action.promptSelectClinicians
      ? t('sidenav.firstSelectCliniciansSessionListToCreateASession')
      : undefined;

  const dismissMobileSidebar = useCallback(() => {
    if (isMobile) {
      setIsMobileHeaderSheetOpen(false);
    }
  }, [isMobile, setIsMobileHeaderSheetOpen]);

  const handleNewSession = useCallback(() => {
    setIsDropdownOpen(false);
    trackCreateMenu('session');
    if (action.mode === 'clinicianPicker') {
      // Defer opening the popover until the menu has fully torn down
      // (onCloseAutoFocus). Opening it now, while the menu is still exit-
      // animating and mounted, lets the closing menu refocus itself on pointer
      // movement — a focusin outside the non-modal popover, which dismisses it
      // (the flicker). The mobile-sheet dismiss is deferred to the member pick:
      // on mobile the "+" (the popover anchor) lives inside the sheet, so
      // closing it here would unmount the anchor before the popover can show.
      openClinicianOnCloseRef.current = true;
      return;
    }
    if (action.mode === 'directCreate') {
      action.onCreate();
      dismissMobileSidebar();
    }
  }, [action, dismissMobileSidebar, trackCreateMenu]);

  const handleCreateForMember = useCallback(
    (memberId: string) => {
      if (action.mode !== 'clinicianPicker') return;
      action.onCreateForMember(memberId);
      dismissMobileSidebar();
    },
    [action, dismissMobileSidebar]
  );

  const handleNewEvidenceChat = useCallback(() => {
    setIsDropdownOpen(false);
    trackCreateMenu('chat');
    handleNewChat();
    dismissMobileSidebar();
  }, [handleNewChat, dismissMobileSidebar, trackCreateMenu]);

  const handleNewTask = useCallback(() => {
    setIsDropdownOpen(false);
    trackCreateMenu('task');
    setCurrentTask(null);
    setActiveSheetType('create');
    setTaskSheetOpen(true);
    void router.push('/tasks');
    dismissMobileSidebar();
  }, [
    router,
    dismissMobileSidebar,
    setCurrentTask,
    setActiveSheetType,
    setTaskSheetOpen,
    trackCreateMenu,
  ]);

  const handleNewRoutine = useCallback(() => {
    setIsDropdownOpen(false);
    trackCreateMenu('routine');
    openRoutineCreateChat();
    dismissMobileSidebar();
  }, [dismissMobileSidebar, openRoutineCreateChat, trackCreateMenu]);

  const handleNewTemplate = useCallback(() => {
    setIsDropdownOpen(false);
    trackCreateMenu('template');
    if (isTemplateCreationRecordEnabled) {
      void router.push('/scribe/my-templates/create-template');
    } else {
      void router.push('/scribe/my-templates');
      setCreateTemplateModal({
        open: true,
        isFrom: 'note_structure_modal',
      });
    }
    dismissMobileSidebar();
  }, [
    router,
    isTemplateCreationRecordEnabled,
    setCreateTemplateModal,
    dismissMobileSidebar,
    trackCreateMenu,
  ]);

  const handleNewPdfForm = useCallback(() => {
    setIsDropdownOpen(false);
    trackCreateMenu('pdf_form');
    setFormFillingUploadModalOpen(true);
    dismissMobileSidebar();
  }, [setFormFillingUploadModalOpen, dismissMobileSidebar, trackCreateMenu]);

  const label = t('sidenav.globalCreate.label');
  const isCreateBusy = isSessionPending;

  const trigger = (
    <DropdownMenuTrigger asChild>
      <SidebarMenuButton
        ref={triggerRef}
        variant="action"
        layout={!isMobile ? 'iconOnly' : 'default'}
        tooltip={!isMobile ? label : undefined}
        aria-label={label}
        className={cn(className)}
        data-testid="sidebar-global-create-dropdown-trigger"
      >
        {isCreateBusy ? <Loader2 className="animate-spin" /> : <Plus />}
        {isMobile && <span>{label}</span>}
      </SidebarMenuButton>
    </DropdownMenuTrigger>
  );

  return (
    <MenuCoachmarkCreateStep>
      <SidebarMenuItem>
        <DropdownMenu
          open={isDropdownOpen}
          onOpenChange={(open) => {
            setIsDropdownOpen(open);
            if (open) trackCreateMenu('open');
          }}
        >
          {action.mode === 'clinicianPicker' ? (
            // Delegation roles pick a clinician before creating. The popover
            // anchors to the persistent "+" (not a menu item, which unmounts on
            // select and would orphan the anchor); the Session menu item just
            // flips `clinicianOpen`. `anchorOnly` keeps the "+" as the dropdown
            // menu's trigger — the popover only borrows its position, so it
            // adds no toggle onClick and does not overwrite the menu ARIA.
            // `contentAlign="start"` matches the left-aligned sidebar.
            <ClinicianSelectorPopover
              host={sessionListHost}
              open={clinicianOpen}
              onOpenChange={setClinicianOpen}
              onSelectClinician={handleCreateForMember}
              contentAlign="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={dropdownSideOffset}
              anchorOnly
            >
              {trigger}
            </ClinicianSelectorPopover>
          ) : (
            trigger
          )}
          <DropdownMenuContent
            className={isMobile ? 'w-[var(--radix-dropdown-menu-trigger-width)]' : 'w-52'}
            side={isMobile ? 'bottom' : 'right'}
            align="start"
            sideOffset={dropdownSideOffset}
            onEscapeKeyDown={() => {
              restoreFocusOnCloseRef.current = true;
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (openClinicianOnCloseRef.current) {
                // Menu teardown is complete — safe to open the picker now; it
                // takes focus without the closing menu stealing it back.
                openClinicianOnCloseRef.current = false;
                setClinicianOpen(true);
                return;
              }
              if (restoreFocusOnCloseRef.current) {
                restoreFocusOnCloseRef.current = false;
                triggerRef.current?.focus();
              }
            }}
          >
            <CreateMenuItems
              headerLabel={t('sidenav.globalCreate.header')}
              sessionLabel={t('sidenav.globalCreate.session')}
              chatLabel={t('sidenav.globalCreate.chat')}
              taskLabel={t('sidenav.globalCreate.task')}
              templateLabel={t('sidenav.globalCreate.template')}
              pdfFormLabel={
                isPdfFormDisabled
                  ? intl.formatMessage(createMenuMessages.xmlForm)
                  : t('sidenav.globalCreate.pdfForm')
              }
              sessionAriaLabel={sessionAriaLabel}
              isSessionDisabled={isSessionDisabled}
              isSessionHidden={isSessionHidden}
              isEvidenceEnabled={isEvidenceEnabled}
              isTasksEnabled={isTasksEnabled}
              isWorkflowsEnabled={isWorkflowsEnabled}
              isOfflineGated={isOfflineGated}
              onNewSession={handleNewSession}
              onNewEvidenceChat={handleNewEvidenceChat}
              onNewTask={handleNewTask}
              onNewRoutine={handleNewRoutine}
              onNewTemplate={handleNewTemplate}
              onNewPdfForm={handleNewPdfForm}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </MenuCoachmarkCreateStep>
  );
}
